import React, { useMemo, useState } from "react";
import { getStoredUserToken } from "../../../utils/functions.ts";

const PURPLE_MAN_ENDPOINT = "https://www.fbxchange.com/ReactPHP/purpleman.php";
const ROSTER_IMPORTS = [
  { key: "CFL_DAN", label: "CFL Dan", league: "CFL", method: "previewCflRosterImport", format: "dan" },
  { key: "CFL_GPT", label: "CFL GPT", league: "CFL", method: "previewCflRosterImport", format: "gpt" },
  { key: "NFL", label: "NFL Update", league: "NFL", method: "previewNflRosterImport", format: "gpt" },
];
const ROSTER_PREVIEW_TIMEOUT_MS = 60000;

const candidateLabel = (candidate) => {
  const details = [candidate.dob, candidate.nflTeam || candidate.cflTeam, candidate.school].filter(Boolean).join(" | ");
  return `${candidate.last}, ${candidate.first}${details ? ` (${details})` : ""}`;
};

const getRosterId = (row) => String(row?.espnId || row?.cflId || "");
const getCurrentRosterId = (candidate) => String(candidate?.currentEspnId || candidate?.currentCflId || "");
const getReviewLeague = (review) => review?.league === "NFL" ? "NFL" : "CFL";
const getRosterIdLabel = (league) => league === "NFL" ? "ESPN ID" : "CFL ID";
const normalizeDob = (value) => {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match && match[1] !== "0000-00-00" ? match[1] : "";
};

const candidateAfterDobUpdate = (candidate, dob) => {
  if (candidate.duplicateName) {
    return { ...candidate, dob, confidence: 25 };
  }

  const hadDob = Boolean(normalizeDob(candidate.dob));
  const evidence = (candidate.evidence || []).filter((item) => (
    item !== "Different birthdate" && item !== "FBX DOB missing" && item !== "Same birthdate"
  ));
  evidence.push("Same birthdate");

  return {
    ...candidate,
    dob,
    confidence: Math.min(100, Number(candidate.confidence || 0) + (hadDob ? 50 : 15)),
    evidence,
  };
};

const confidenceClass = (confidence) => {
  if (confidence >= 80) return "high";
  if (confidence >= 60) return "medium";
  return "low";
};

const RosterImports = ({ adminUser }) => {
  const [files, setFiles] = useState({});
  const [review, setReview] = useState(null);
  const [activeTeam, setActiveTeam] = useState("");
  const [selections, setSelections] = useState({});
  const [savingRosterId, setSavingRosterId] = useState("");
  const [savingDobId, setSavingDobId] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLeague, setLoadingLeague] = useState("");
  const [message, setMessage] = useState("");

  const teamRows = useMemo(
    () => review?.rows?.filter((row) => row.team === activeTeam) || [],
    [activeTeam, review]
  );

  const perfectTeamMatches = useMemo(() => teamRows.reduce((matches, row) => {
    if (row.rosterIdCurrent) return matches;
    const selectedId = selections[getRosterId(row)] || "";
    const candidate = row.candidates?.find((item) => String(item.fbxId) === String(selectedId));
    if (candidate && Number(candidate.confidence) === 100) matches.push({ row, candidate });
    return matches;
  }, []), [selections, teamRows]);

  const previewRoster = async (file, rosterImport) => {
    const { league } = rosterImport;
    setLoading(true);
    setLoadingLeague(rosterImport.key);
    setMessage(`Reading ${rosterImport.label} roster...`);
    setReview(null);
    setSelections({});

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), ROSTER_PREVIEW_TIMEOUT_MS);

    try {
      const form = new FormData();
      form.append("method", rosterImport.method);
      form.append("rosterFormat", rosterImport.format);
      form.append("user", adminUser);
      form.append("token", getStoredUserToken() || "");
      form.append("roster", file);

      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        body: form,
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        const errorMessage = data?.error || `Unable to review ${league} roster.`;
        window.alert(errorMessage);
        setMessage(errorMessage);
        return;
      }

      const initialSelections = {};
      data.rows.forEach((row) => {
        if (row.candidates?.[0]?.fbxId) {
          initialSelections[getRosterId(row)] = String(row.candidates[0].fbxId);
        }
      });

      setReview({ ...data, league });
      setSelections(initialSelections);
      setActiveTeam(data.teams?.[0] || "");
      setMessage(data.rows.length === 0 ? `No ${getRosterIdLabel(league)} or DOB updates were found.` : "");
    } catch (error) {
      console.warn(`${league} roster preview failed:`, error);
      const errorMessage = error?.name === "AbortError"
        ? `The ${league} roster comparison took too long. Please try again after the server update is installed.`
        : `Unable to review ${league} roster.`;
      window.alert(errorMessage);
      setMessage(errorMessage);
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
      setLoadingLeague("");
    }
  };

  const selectRosterFile = (rosterImport, event) => {
    const file = event.target.files?.[0] || null;
    setFiles((current) => ({ ...current, [rosterImport.key]: file }));
    setMessage("");

    if (file) {
      previewRoster(file, rosterImport);
    }
  };

  const removeRosterRow = (rosterId) => {
    const remainingRows = review.rows.filter((row) => getRosterId(row) !== rosterId);
    const remainingTeams = review.teams.filter((team) => remainingRows.some((row) => row.team === team));
    setReview({ ...review, rows: remainingRows, teams: remainingTeams });
    if (!remainingTeams.includes(activeTeam)) setActiveTeam(remainingTeams[0] || "");
  };

  const submitRosterId = async (row, candidate) => {
    const league = getReviewLeague(review);
    const rosterId = getRosterId(row);
    const currentRosterId = getCurrentRosterId(candidate);
    const action = currentRosterId ? "Update" : "Add";
    const form = new URLSearchParams({
      method: league === "NFL" ? "applyNflRosterId" : "applyCflRosterId",
      user: String(adminUser || ""),
      token: getStoredUserToken() || "",
      fbxId: String(candidate.fbxId),
      action,
    });
    if (league === "NFL") {
      form.set("espnId", rosterId);
      form.set("expectedEspnId", currentRosterId);
    } else {
      form.set("cflId", rosterId);
      form.set("expectedCflId", currentRosterId);
    }
    const response = await fetch(PURPLE_MAN_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    });
    const data = await response.json();
    return {
      action,
      success: response.ok && Boolean(data?.success),
      error: data?.error || `Unable to ${action.toLowerCase()} ${getRosterIdLabel(league)}.`,
    };
  };

  const saveRosterId = async (row, candidate) => {
    if (!candidate || savingRosterId || savingDobId) return;

    const league = getReviewLeague(review);
    const rosterId = getRosterId(row);
    const currentRosterId = getCurrentRosterId(candidate);
    const idLabel = getRosterIdLabel(league);
    const action = currentRosterId ? "Update" : "Add";
    if (candidate.duplicateName) {
      const confirmed = window.confirm(
        `Confirm ${idLabel} ${rosterId} for ${candidate.last}, ${candidate.first}? This is a 25% duplicate-name match and requires manual verification.`
      );
      if (!confirmed) return;
    } else if (action === "Update") {
      const confirmed = window.confirm(
        `Update ${candidate.last}, ${candidate.first} from ${idLabel} ${currentRosterId} to ${rosterId}?`
      );
      if (!confirmed) return;
    }

    setSavingRosterId(rosterId);
    setMessage("");

    try {
      const result = await submitRosterId(row, candidate);
      if (!result.success) {
        setMessage(result.error);
        return;
      }

      removeRosterRow(rosterId);
      setMessage(`${action} complete: ${idLabel} ${rosterId} saved to FBX player ${candidate.fbxId}.`);
    } catch (error) {
      console.warn(`${idLabel} update failed:`, error);
      setMessage(`Unable to ${action.toLowerCase()} ${idLabel}.`);
    } finally {
      setSavingRosterId("");
    }
  };

  const saveRosterDob = async (row, candidate) => {
    if (!candidate || savingRosterId || savingDobId) return;

    const rosterId = getRosterId(row);
    const uploadedDob = normalizeDob(row.birthdate);
    const currentDob = normalizeDob(candidate.dob);
    if (!uploadedDob || uploadedDob === currentDob) return;

    const confirmed = window.confirm(
      `Update ${candidate.last}, ${candidate.first} DOB from ${currentDob || "no DOB"} to ${uploadedDob}?`
    );
    if (!confirmed) return;

    setSavingDobId(rosterId);
    setMessage("");

    try {
      const form = new URLSearchParams({
        method: "applyRosterDob",
        user: String(adminUser || ""),
        token: getStoredUserToken() || "",
        fbxId: String(candidate.fbxId),
        dob: uploadedDob,
        expectedDob: currentDob,
      });
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        setMessage(data?.error || "Unable to update DOB.");
        return;
      }

      setReview((current) => ({
        ...current,
        rows: current.rows.map((currentRow) => getRosterId(currentRow) !== rosterId
          ? currentRow
          : {
            ...currentRow,
            candidates: currentRow.candidates.map((item) => String(item.fbxId) === String(candidate.fbxId)
              ? candidateAfterDobUpdate(item, uploadedDob)
              : item),
          }),
      }));
      setMessage(`DOB updated to ${uploadedDob} for FBX player ${candidate.fbxId}.`);
    } catch (error) {
      console.warn("DOB update failed:", error);
      setMessage("Unable to update DOB.");
    } finally {
      setSavingDobId("");
    }
  };

  const processPerfectMatches = async () => {
    if (savingRosterId || savingDobId || perfectTeamMatches.length === 0) return;

    const teamAtStart = activeTeam;
    const successfulIds = [];
    const failures = [];
    setSavingRosterId("bulk");
    setMessage("");

    for (let index = 0; index < perfectTeamMatches.length; index += 1) {
      const { row, candidate } = perfectTeamMatches[index];
      setBulkProgress({ current: index + 1, total: perfectTeamMatches.length });
      try {
        const result = await submitRosterId(row, candidate);
        if (result.success) {
          successfulIds.push(getRosterId(row));
        } else {
          failures.push(`${row.name}: ${result.error}`);
        }
      } catch (error) {
        console.warn(`Bulk ${getRosterIdLabel(getReviewLeague(review))} update failed:`, error);
        failures.push(`${row.name}: request failed`);
      }
    }

    const successfulSet = new Set(successfulIds);
    const remainingRows = review.rows.filter((row) => !successfulSet.has(getRosterId(row)));
    const remainingTeams = review.teams.filter((team) => remainingRows.some((row) => row.team === team));
    setReview({ ...review, rows: remainingRows, teams: remainingTeams });
    if (!remainingTeams.includes(teamAtStart)) setActiveTeam(remainingTeams[0] || "");

    const summary = `Processed ${successfulIds.length} of ${perfectTeamMatches.length} 100% matches for ${teamAtStart}.`;
    setMessage(failures.length > 0 ? `${summary} ${failures.length} failed: ${failures.join(" | ")}` : summary);
    setBulkProgress(null);
    setSavingRosterId("");
  };

  const reviewLeague = getReviewLeague(review);
  const reviewIdLabel = getRosterIdLabel(reviewLeague);

  return (
    <>
      <div className="purpleman-admin-roster-toolbar">
        <strong>Roster Imports</strong>
        <div className="purpleman-admin-roster-actions">
          {ROSTER_IMPORTS.map((rosterImport) => (
            <div className="purpleman-admin-roster-control" key={rosterImport.key}>
              <label className="btn btn-sm btn-outline-primary">
                {loading && loadingLeague === rosterImport.key ? "Reading..." : rosterImport.label}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  disabled={loading || Boolean(savingRosterId) || Boolean(savingDobId)}
                  onChange={(event) => selectRosterFile(rosterImport, event)}
                />
              </label>
              <span title={files[rosterImport.key]?.name || "No file selected"}>
                {files[rosterImport.key]?.name || "No file selected"}
              </span>
            </div>
          ))}
        </div>
        {message && <div className="purpleman-admin-roster-message">{message}</div>}
      </div>

      {review && review.rows.length > 0 && (
        <div className="purpleman-roster-review-backdrop">
          <div className="purpleman-roster-review" role="dialog" aria-modal="true" aria-labelledby="roster-review-title">
            <div className="purpleman-roster-review__header">
              <div className="purpleman-roster-review__header-title">
                <strong id="roster-review-title">{reviewLeague} Roster Review</strong>
                <span>{review.fileName} | {review.rows.length} players to review</span>
              </div>
              <div className="purpleman-roster-review__header-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={processPerfectMatches}
                  disabled={Boolean(savingRosterId) || Boolean(savingDobId) || perfectTeamMatches.length === 0}
                  title={`Process ${perfectTeamMatches.length} selected 100% matches for ${activeTeam}`}
                >
                  {savingRosterId === "bulk" && bulkProgress
                    ? `Processing ${bulkProgress.current}/${bulkProgress.total}`
                    : "Process all 100%"}
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setReview(null)} disabled={Boolean(savingRosterId) || Boolean(savingDobId)}>
                  Close
                </button>
              </div>
            </div>

            {message && <div className="purpleman-roster-review__notice">{message}</div>}

            <div className="purpleman-roster-review__teams">
              {review.teams.map((team) => {
                const count = review.rows.filter((row) => row.team === team).length;
                return (
                  <button
                    type="button"
                    key={team}
                    className={activeTeam === team ? "purpleman-roster-review__team purpleman-roster-review__team--active" : "purpleman-roster-review__team"}
                    onClick={() => setActiveTeam(team)}
                  >
                    {team} <span>{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="purpleman-roster-review__columns">
              <span>{reviewLeague} roster player</span>
              <span>FBX match and confidence</span>
              <span>Action</span>
            </div>

            <div className="purpleman-roster-review__body">
              {teamRows.map((row) => {
                const rosterId = getRosterId(row);
                const selectedId = selections[rosterId] || "";
                const candidate = row.candidates?.find((item) => String(item.fbxId) === String(selectedId));
                const currentRosterId = getCurrentRosterId(candidate);
                const action = currentRosterId ? "Update" : "Add";
                const isDuplicateName = Boolean(candidate?.duplicateName);
                const actionLabel = isDuplicateName ? "CONFIRM" : action;
                const uploadedDob = normalizeDob(row.birthdate);
                const currentDob = normalizeDob(candidate?.dob);
                const canUpdateDob = Boolean(candidate && uploadedDob && uploadedDob !== currentDob);

                return (
                  <div
                    className={isDuplicateName
                      ? "purpleman-roster-review__row purpleman-roster-review__row--duplicate"
                      : "purpleman-roster-review__row"}
                    key={rosterId}
                  >
                    <div className="purpleman-roster-review__source">
                      <strong>{row.name}</strong>
                      <span>{[row.position, row.birthdate, row.college].filter(Boolean).join(" | ")}</span>
                      <span>{reviewIdLabel} {rosterId}</span>
                    </div>

                    <div className="purpleman-roster-review__match">
                      {row.candidates?.length > 0 ? (
                        <>
                          <select value={selectedId} onChange={(event) => setSelections((current) => ({ ...current, [rosterId]: event.target.value }))}>
                            <option value="">No FBX match</option>
                            {row.candidates.map((item) => (
                              <option value={item.fbxId} key={item.fbxId}>
                                {candidateLabel(item)} | FBX {item.fbxId}
                              </option>
                            ))}
                          </select>
                          {candidate && (
                            <>
                              <div className="purpleman-roster-fbx-details">
                                FBX: {candidate.nflTeam || candidate.cflTeam || "No team"} | {candidate.school || "No school"}
                              </div>
                              <div className={`purpleman-roster-confidence purpleman-roster-confidence--${confidenceClass(candidate.confidence)}${isDuplicateName ? " purpleman-roster-confidence--duplicate" : ""}`}>
                                <strong>{candidate.confidence}%</strong>
                                <span>{candidate.evidence.join(" | ")}</span>
                              </div>
                            </>
                          )}
                          {currentRosterId && (
                            <div className="purpleman-roster-conflict">Current {reviewIdLabel}: {currentRosterId}</div>
                          )}
                        </>
                      ) : (
                        <span>No FBX candidate found</span>
                      )}
                    </div>

                    <div className="purpleman-roster-review__action">
                      {!row.rosterIdCurrent && (
                        <button
                          type="button"
                          className={isDuplicateName
                            ? "btn btn-sm purpleman-roster-confirm"
                            : action === "Update" ? "btn btn-sm btn-warning" : "btn btn-sm btn-success"}
                          onClick={() => saveRosterId(row, candidate)}
                          disabled={!candidate || Boolean(savingRosterId) || Boolean(savingDobId)}
                        >
                          {savingRosterId === rosterId ? "Saving..." : actionLabel}
                        </button>
                      )}
                      {canUpdateDob && (
                        <button
                          type="button"
                          className="btn btn-sm purpleman-roster-dob"
                          onClick={() => saveRosterDob(row, candidate)}
                          disabled={Boolean(savingRosterId) || Boolean(savingDobId)}
                          title={`Change FBX DOB from ${currentDob || "none"} to ${uploadedDob}`}
                        >
                          {savingDobId === rosterId ? "Saving DOB..." : "Update DOB"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => removeRosterRow(rosterId)}
                        disabled={Boolean(savingRosterId) || Boolean(savingDobId)}
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RosterImports;
